import { Fragment, useState } from "react";
import { CircleMarker, Marker, Polygon, Polyline, Tooltip, useMapEvents } from "react-leaflet";
import L from "leaflet";

const CAT_COLOR = { "Cat 5": "#ff00c8", "Cat 4": "#ff2a2a", "Cat 3": "#ff7a00", "Cat 2": "#ffb000", "Cat 1": "#ffe14d", TS: "#4dd0ff", TD: "#7dd3fc" };
const fmtPeople = (n) => (n >= 1e6 ? `${(n / 1e6).toFixed(1)}M` : n >= 1e3 ? `${Math.round(n / 1e3)}k` : String(n || 0));

export const impactLine = (s) =>
  s.impact
    ? `${fmtPeople(s.impact.population)} people · ${s.impact.cities_count} cities · ${s.impact.plants_count} plants in ${s.impact.basis}`
    : null;

const horizonH = (s) => (s.wind_horizon ? Math.max(0, Math.round((new Date(s.wind_horizon) - Date.now()) / 36e5)) : null);

export const etaText = (c, s) => {
  if (c.status === "passed") return `TS winds passed ${Math.round(c.passed_h_ago)} h ago`;
  if (c.status === "in_ts_winds") return "tropical-storm-force winds NOW";
  if (c.status === "arriving") return `TS winds in ~${Math.max(1, Math.round(c.ts_winds_in_h))} h`;
  if (c.status === "beyond_forecast") return `TS winds > ${horizonH(s) ?? "?"} h (beyond NHC wind-radii forecast)`;
  return "no NHC wind-radii forecast";
};

const soonest = (s) =>
  (s.impact?.cities || [])
    .filter((c) => c.status === "arriving" || c.status === "in_ts_winds")
    .sort((a, b) => (a.ts_winds_in_h ?? 0) - (b.ts_winds_in_h ?? 0));

export const cycloneColor = (s) => CAT_COLOR[s.category] || (s.alert_level === "Red" ? "#ff2a2a" : s.alert_level === "Orange" ? "#ff9500" : "#a5b4fc");

const icon = (s) =>
  L.divIcon({
    className: "",
    iconSize: [34, 34],
    iconAnchor: [17, 17],
    html: `<div class="atlas-cyclone" data-testid="cyclone-marker-${s.id}" style="--c:${cycloneColor(s)}"><span>${(s.category || "TC").replace("Cat ", "")}</span></div>`,
  });

const RAIN_COLOR = { warning: "#F43F5E", watch: "#38BDF8" };
const rainFor = (s, c) => (s.rain_outlook?.towns || []).find((t) => t.name === c.name && Math.abs(t.lat - c.lat) < 0.01);

const RainLine = ({ s }) => {
  const ro = s.rain_outlook;
  if (!ro) return null;
  return (
    <>
      <br />
      <span style={{ color: "#38BDF8" }}>
        Rain outlook (72 h): {ro.warning} warning · {ro.watch} watch towns
      </span>
      {ro.towns.slice(0, 4).map((t) => (
        <span key={t.name} style={{ display: "block", color: RAIN_COLOR[t.level] || undefined }}>
          · {t.name}: {Math.round(t.mm_72h)} mm{t.level ? ` · ${t.level.toUpperCase()}` : ""}
        </span>
      ))}
      <span className="atlas-tip-muted" style={{ display: "block" }}>{ro.label} · {ro.models}</span>
    </>
  );
};

const StormTip = ({ s }) => (
  <Tooltip direction="top" offset={[0, -16]} className="atlas-tip" opacity={1}>
    <b>{s.classification_label} {s.name}</b>
    <br />
    {s.category}
    {s.wind_kt ? ` · ${Math.round(s.wind_kt)} kt (${s.wind_mph ?? Math.round(s.wind_kt * 1.15)} mph)` : ""}
    {s.pressure_mb ? ` · ${s.pressure_mb} mb` : ""}
    {s.movement && (
      <>
        <br />
        Moving {s.movement}
      </>
    )}
    {s.impact && (
      <>
        <br />
        <span style={{ color: "#FBBF24" }}>{impactLine(s)}</span>
        {soonest(s).slice(0, 5).map((c) => (
          <span key={c.name} style={{ display: "block" }}>
            · {c.name}: {etaText(c, s)}
          </span>
        ))}
      </>
    )}
    <RainLine s={s} />
    <br />
    <span className="atlas-tip-muted">{s.source}{s.last_update ? ` · ${new Date(s.last_update).toUTCString().slice(5, 22)} UTC` : ""}</span>
  </Tooltip>
);

const useZoom = () => {
  const [zoom, setZoom] = useState(2);
  const map = useMapEvents({ zoomend: () => setZoom(map.getZoom()) });
  return zoom;
};

export const CycloneLayer = ({ storms = [], onSelect }) => {
  const showLabels = useZoom() >= 5;
  return storms.map((s) => (
    <Fragment key={s.id}>
      {s.cone?.length > 2 && (
        <Polygon positions={s.cone} interactive={false}
          pathOptions={{ color: "#ffffff", weight: 1, dashArray: "4 4", fillColor: "#ffffff", fillOpacity: 0.12 }} />
      )}
      {s.track?.line?.length > 1 && (
        <Polyline positions={s.track.line} interactive={false} pathOptions={{ color: cycloneColor(s), weight: 2.5, dashArray: "6 5" }} />
      )}
      {(s.track?.points || []).slice(1).map((p, i) => (
        <CircleMarker key={i} center={[p.lat, p.lon]} radius={4}
          pathOptions={{ color: "#05070a", weight: 1, fillColor: cycloneColor({ category: p.wind_kt >= 64 ? "Cat 1" : "TS" }), fillOpacity: 1 }}>
          <Tooltip direction="top" className="atlas-tip" opacity={1}>
            <b>{s.name} forecast</b>
            <br />
            {p.valid}
            {p.wind_kt ? ` · ${p.wind_kt} kt` : ""}
          </Tooltip>
        </CircleMarker>
      ))}
      {[...(s.impact?.cities || []), ...(s.impact?.passed || [])].map((c) => {
        const passed = c.status === "passed";
        const near = showLabels && soonest(s).slice(0, 6).includes(c);
        const rain = !passed && rainFor(s, c);
        return (
          <Fragment key={`${s.id}-${c.name}-${c.lat}`}>
          {rain?.level && (
            <CircleMarker center={[c.lat, c.lon]} radius={11 + Math.min(6, (c.population || 0) / 4e5)} interactive={false}
              className={`atlas-rain-ring-${rain.level}`}
              pathOptions={{ color: RAIN_COLOR[rain.level], weight: 2, dashArray: "3 3", fillOpacity: 0 }} />
          )}
          <CircleMarker center={[c.lat, c.lon]} radius={5 + Math.min(6, (c.population || 0) / 4e5)}
            className={passed ? "atlas-cone-city-passed" : "atlas-cone-city"}
            pathOptions={passed ? { color: "#8B98A5", weight: 1, fillColor: "#8B98A5", fillOpacity: 0.35 }
              : { color: "#FBBF24", weight: 1.5, fillColor: "#FBBF24", fillOpacity: 0.55 }}>
            <Tooltip direction="top" className="atlas-tip atlas-eta" opacity={1} permanent={near} key={near ? "p" : "h"}>
              <b>{c.name}</b>
              {near ? ` · ${etaText(c, s).replace("TS winds in ", "")}` : (
                <>
                  <br />
                  {etaText(c, s)}
                  <br />
                  ~{fmtPeople(c.population)} people · {passed ? "storm has passed" : `inside ${s.name}'s ${s.impact.basis}`}
                  {rain && (
                    <>
                      <br />
                      <span style={{ color: RAIN_COLOR[rain.level] || "#38BDF8" }}>
                        Forecast rain 72 h: {Math.round(rain.mm_72h)} mm{rain.level ? ` · ${rain.level.toUpperCase()}` : ""} (ECMWF {rain.ecmwf_mm ?? "—"} · GFS {rain.gfs_mm ?? "—"})
                      </span>
                      <br />
                      <span className="atlas-tip-muted">Model forecast · not scored</span>
                    </>
                  )}
                </>
              )}
            </Tooltip>
          </CircleMarker>
          </Fragment>
        );
      })}
      <Marker position={[s.lat, s.lon]} icon={icon(s)} eventHandlers={{ click: () => onSelect && onSelect(s) }}>
        <StormTip s={s} />
      </Marker>
    </Fragment>
  ));
};
